/**
 * @license Copyright (c) 2003-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import { ContextualBalloon, clickOutsideHandler } from '@ckeditor/ckeditor5-ui';
import FormView from './referenceView';
import '../static/styles.css';
import referenceIcon from '../static/reference_icon.svg';
import { getRangeText, addTranslations } from './utils.js';


export default class ReferenceUi extends Plugin {
	static get requires() {
		return [ ContextualBalloon ];
	}

	init() {
		const editor = this.editor;

        // Create the balloon and the form view.
		this._balloon = this.editor.plugins.get( ContextualBalloon );
		this.formView = this._createFormView();

		editor.ui.componentFactory.add( 'reference', locale => {
			const button = new ButtonView( locale );

			const { t } = editor.locale;

			addTranslations("fr", { 'Reference': 'Renvoi' })

			button.set( {
				class: 'add-reference-button',
				isEnabled: true,
				label: t( 'Reference' ),
				icon: referenceIcon,
				tooltip: true
			} );

			// Show the UI on button click.
			this.listenTo( button, 'execute', () => {
				this._showUI();
			} );

			return button;
		} );
	}

	_createFormView() {
		const editor = this.editor;
		const formView = new FormView( editor.locale );

		// Execute the command after clicking the "Save" button.
		this.listenTo( formView, 'submit', () => {
			// Grab values from the reference input field.
			const reference = formView.referenceInputView.fieldView.element.value;

			editor.execute( 'addReference', reference );

            // Hide the form view after submit.
			this._hideUI();
		} );

		// Execute the command after clicking the "RemoveReference" button.
		this.listenTo( formView, 'removeReference', () => {
			editor.execute( 'removeReference' );

            // Hide the form view after submit.
			this._hideUI();
		} );

		// Hide the form view after clicking the "Cancel" button.
		this.listenTo( formView, 'cancel', () => {
			this._hideUI();
		} );

		// Hide the form view when clicking outside the balloon.
		clickOutsideHandler( {
			emitter: formView,
			activator: () => this._balloon.visibleView === formView,
			contextElements: [ this._balloon.view.element ],
			callback: () => this._hideUI()
		} );

		return formView;
	}

	_showUI() {
		const selection = this.editor.model.document.selection;

		// Check the value of the command.
		const commandValue = this.editor.commands.get( 'addReference' ).value;

		this._balloon.add( {
			view: this.formView,
			position: this._getBalloonPositionData()
		} );

		// Fill the form using the state (value) of the command.
		if ( commandValue ) {
			this.formView.referenceInputView.fieldView.value = commandValue.reference;
		}
		// If the command has no value, put the currently selected text (not collapsed)
		// in the field
		else {
			const selectedText = getRangeText( selection.getFirstRange() );

			this.formView.referenceInputView.fieldView.value = selectedText;
		}

		// Disable Remove Reference button if selection is not a reference
		const selectedElement = selection.getSelectedElement();
		if ( !selectedElement || selectedElement.name !== 'reference' ) {
			this.formView.removeReferenceButtonView.isEnabled = false;
		} else {
			this.formView.removeReferenceButtonView.isEnabled = true;
		}

		this.formView.focus();
	}

	_hideUI() {
		// Clear the input field values and reset the form.parent.name !== 'reference'
		this.formView.referenceInputView.fieldView.value = '';
		this.formView.element.reset();

		this._balloon.remove( this.formView );

		// Focus the editing view after inserting the reference so the user can start typing the content
		// right away and keep the editor focused.
		this.editor.editing.view.focus();
	}

	_getBalloonPositionData() {
		const view = this.editor.editing.view;
		const viewDocument = view.document;
		let target = null;

		// Set a target position by converting view selection range to DOM
		target = () => view.domConverter.viewRangeToDom( viewDocument.selection.getFirstRange() );

		return {
			target
		};
	}
}